import React from 'react';
import './RoleCardIllustration.css';

export type RoleIllustrationType = 'rapper' | 'producer' | 'listener';

interface Props {
  role: RoleIllustrationType;
  active?: boolean;
}

const RoleCardIllustration: React.FC<Props> = ({ role, active = true }) => {
  return (
    <div
      className={`role-illus role-illus--${role} ${active ? 'role-illus--active' : ''}`}
      aria-hidden
    >
      {role === 'rapper' && <RapperScene />}
      {role === 'producer' && <ProducerScene />}
      {role === 'listener' && <ListenerScene />}
    </div>
  );
};

/** Stylized rapper — cap, mic, bob + lyric bars */
const RapperScene: React.FC = () => (
  <svg className="role-illus__svg" viewBox="0 0 280 240" fill="none">
    <ellipse className="role-illus__shadow" cx="140" cy="218" rx="72" ry="10" fill="currentColor" opacity="0.2" />
    <g className="role-illus__rapper-body">
      {/* legs */}
      <path d="M118 175 L118 205 M162 175 L162 205" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M108 205 h52" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      {/* torso */}
      <path d="M108 118 Q140 108 172 118 L168 175 L112 175 Z" fill="currentColor" opacity="0.85" />
      {/* chain */}
      <path d="M125 128 Q140 138 155 128" stroke="#f91942" strokeWidth="2" fill="none" opacity="0.9" />
      {/* head */}
      <circle cx="140" cy="92" r="28" fill="currentColor" opacity="0.9" />
      {/* cap */}
      <path d="M108 88 Q140 68 172 88 L168 78 Q140 58 112 78 Z" fill="#1e293b" />
      <path d="M108 88 h64" stroke="#1e293b" strokeWidth="4" />
      {/* shades */}
      <rect x="122" y="86" width="36" height="8" rx="4" fill="#1e293b" />
      {/* mic arm */}
      <g className="role-illus__rapper-mic-arm">
        <path d="M172 118 L195 95" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <rect x="188" y="72" width="22" height="32" rx="11" fill="currentColor" />
        <line x1="199" y1="72" x2="199" y2="58" stroke="currentColor" strokeWidth="3" />
        <ellipse cx="199" cy="54" rx="14" ry="10" stroke="currentColor" strokeWidth="2" fill="rgba(249,25,66,0.3)" />
      </g>
      {/* free hand gesture */}
      <path d="M98 130 Q85 115 92 100" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
    </g>
    {/* sound / lyric waves */}
    {[0, 1, 2, 3, 4].map((i) => (
      <path
        key={i}
        className="role-illus__sound-wave"
        d={`M ${212 + i * 10} ${88 + (i % 2) * 6} Q ${225 + i * 10} 92 ${212 + i * 10} ${108 - (i % 2) * 6}`}
        stroke="#f91942"
        strokeWidth="2"
        opacity="0.7"
        style={{ animationDelay: `${i * 0.1}s` }}
      />
    ))}
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <rect
        key={`bar-${i}`}
        className="role-illus__eq-bar"
        x={28 + i * 14}
        y={160 - (i % 3) * 8}
        width="8"
        height={24 + (i % 4) * 12}
        rx="3"
        fill="#f91942"
        opacity="0.5"
        style={{ animationDelay: `${i * 0.07}s` }}
      />
    ))}
  </svg>
);

/** Producer at beat pad — nodding, glowing pads */
const ProducerScene: React.FC = () => (
  <svg className="role-illus__svg" viewBox="0 0 280 240" fill="none">
    <ellipse className="role-illus__shadow" cx="140" cy="218" rx="80" ry="10" fill="currentColor" opacity="0.2" />
    {/* desk / MPC */}
    <rect x="40" y="155" width="200" height="50" rx="8" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5" />
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <rect
        key={i}
        className="role-illus__pad"
        x={52 + (i % 4) * 22}
        y={i < 4 ? 165 : 178}
        width="14"
        height="14"
        rx="3"
        fill="currentColor"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
    <g className="role-illus__producer-body">
      <path d="M115 175 L115 200 M165 175 L165 200" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M105 200 h70" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M112 115 Q140 105 168 115 L165 175 L115 175 Z" fill="currentColor" opacity="0.85" />
      <circle className="role-illus__producer-head" cx="140" cy="88" r="26" fill="currentColor" opacity="0.9" />
      {/* headphones */}
      <path d="M108 88 Q108 62 140 58 Q172 62 172 88" stroke="currentColor" strokeWidth="3" fill="none" />
      <rect x="98" y="78" width="16" height="28" rx="6" fill="#3498db" opacity="0.8" />
      <rect x="166" y="78" width="16" height="28" rx="6" fill="#3498db" opacity="0.8" />
      {/* hands on pads */}
      <ellipse cx="115" cy="152" rx="10" ry="8" fill="currentColor" opacity="0.7" />
      <ellipse cx="165" cy="152" rx="10" ry="8" fill="currentColor" opacity="0.7" />
    </g>
    {/* waveform on desk */}
    <path
      className="role-illus__waveform"
      d="M 55 148 Q 80 138 105 148 T 155 148 T 205 148 T 225 148"
      stroke="#3498db"
      strokeWidth="2"
      fill="none"
    />
    <circle className="role-illus__orbit role-illus__orbit--1" cx="220" cy="100" r="4" fill="#3498db" />
    <circle className="role-illus__orbit role-illus__orbit--2" cx="235" cy="120" r="3" fill="#3498db" opacity="0.6" />
  </svg>
);

/** Listener vibing — headphones, vote hearts */
const ListenerScene: React.FC = () => (
  <svg className="role-illus__svg" viewBox="0 0 280 240" fill="none">
    <ellipse className="role-illus__shadow" cx="140" cy="218" rx="70" ry="10" fill="currentColor" opacity="0.2" />
    <g className="role-illus__listener-body">
      <path d="M120 172 L120 202 M160 172 L160 202" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M110 202 h60" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M114 118 Q140 112 166 118 L163 172 L117 172 Z" fill="currentColor" opacity="0.85" />
      <g className="role-illus__listener-head">
        <circle cx="140" cy="90" r="27" fill="currentColor" opacity="0.9" />
        <path d="M108 92 Q108 58 140 52 Q172 58 172 92" stroke="currentColor" strokeWidth="4" fill="none" />
        <rect x="96" y="82" width="18" height="32" rx="8" fill="#27ae60" opacity="0.85" />
        <rect x="166" y="82" width="18" height="32" rx="8" fill="#27ae60" opacity="0.85" />
        {/* closed eyes vibing */}
        <path d="M128 92 Q132 96 136 92" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" />
        <path d="M144 92 Q148 96 152 92" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" />
        {/* smile */}
        <path d="M130 102 Q140 110 150 102" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" fill="none" />
      </g>
      {/* phone */}
      <rect x="168" y="128" width="28" height="48" rx="4" fill="currentColor" opacity="0.4" stroke="currentColor" strokeWidth="2" />
      <rect x="172" y="138" width="20" height="28" rx="2" fill="#27ae60" opacity="0.3" />
      {/* arm sway */}
      <path className="role-illus__listener-arm" d="M108 125 Q90 140 95 155" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
    </g>
    {/* floating hearts / votes */}
    {[0, 1, 2].map((i) => (
      <g key={i} className="role-illus__heart-float" style={{ animationDelay: `${i * 0.4}s` }}>
        <path
          d={`M ${50 + i * 35} 70 c 0 -6 8 -10 12 -4 c 4 -6 12 -2 12 4 c 0 8 -12 16 -12 16 c 0 0 -12 -8 -12 -16 z`}
          fill="#27ae60"
          opacity="0.75"
        />
      </g>
    ))}
    {/* music notes */}
    <text className="role-illus__note role-illus__note--1" x="210" y="75" fill="currentColor" opacity="0.5" fontSize="20">♪</text>
    <text className="role-illus__note role-illus__note--2" x="230" y="95" fill="currentColor" opacity="0.4" fontSize="16">♫</text>
  </svg>
);

export default RoleCardIllustration;
